import type {
  RemixCreditProductInfo,
  RemixCreditQuota,
  RemixUpgradeModalMode,
  RemixUpgradeModalOpenOptions,
  RemixUpgradeModalState,
  RemixUpgradeModalStore,
} from './types';
import { CREDITS_PER_UNIT } from '@wegic/price-ui';
import { create } from 'zustand';
import {
  REMIX_MAX_CREDITS,
  REMIX_MAX_PACKS,
} from '@/components/RemixUpgradeModal/const';
import { getCreditPurchaseQuotaRequest, getRemixCreditProductRequest } from '@/services/pay/api';

const DEFAULT_OPEN_OPTIONS: Required<RemixUpgradeModalOpenOptions> = {
  defaultTab: 'credits',
  visibleTabs: ['credits', 'subscription'],
};

function normalizeOpenOptions(
  input?: RemixUpgradeModalMode | RemixUpgradeModalOpenOptions,
): Required<RemixUpgradeModalOpenOptions> {
  if (!input) {
    return { ...DEFAULT_OPEN_OPTIONS };
  }
  if (typeof input === 'string') {
    return { ...DEFAULT_OPEN_OPTIONS, defaultTab: input };
  }
  return {
    defaultTab: input.defaultTab ?? DEFAULT_OPEN_OPTIONS.defaultTab,
    visibleTabs: input.visibleTabs ?? DEFAULT_OPEN_OPTIONS.visibleTabs,
  };
}

const initialState: RemixUpgradeModalState = {
  visible: false,
  mode: 'credits',
  selectedCredits: CREDITS_PER_UNIT,
  creditPurchaseQuota: null,
  product: null,
  productInfo: null,
  quota: null,
  loading: false,
  error: null,
  openOptions: DEFAULT_OPEN_OPTIONS,
};

let quotaRequestId = 0;
let productRequest: Promise<{
  ok: boolean;
  productInfo: RemixCreditProductInfo | null;
  product: Awaited<ReturnType<typeof getRemixCreditProductRequest>>['data'] | null;
}> | null = null;

function buildQuota(purchased: number): RemixCreditQuota {
  const safe = Math.max(0, purchased);
  const remaining = Math.max(0, REMIX_MAX_CREDITS - safe);
  const maxPacks = Math.min(REMIX_MAX_PACKS, Math.floor(remaining / CREDITS_PER_UNIT));
  return { purchased: safe, remaining, maxPacks };
}

/**
 * 拉取商品信息。商品信息成功后会缓存在 store 中，后续不重复拉取。
 * 返回结构化结果，不抛错
 */
async function fetchProduct(): Promise<{
  ok: boolean;
  productInfo: RemixCreditProductInfo | null;
  product: Awaited<ReturnType<typeof getRemixCreditProductRequest>>['data'] | null;
}> {
  try {
    const productRes = await getRemixCreditProductRequest();

    let productInfo: RemixCreditProductInfo | null = null;
    let product: typeof productRes.data | null = null;
    if (productRes.code === 0 && productRes.data) {
      const price = productRes.data.prices?.[0];
      if (price) {
        product = productRes.data;
        productInfo = {
          productId: productRes.data.id,
          priceId: price.id,
          unitPrice: (price.amount ?? 0) / 100,
          currencySymbol: price.currencySymbol || '$',
        };
      }
    }

    return {
      ok: productInfo !== null,
      productInfo,
      product,
    };
  } catch {
    return { ok: false, productInfo: null, product: null };
  }
}

export const useRemixUpgradeModalStore = create<RemixUpgradeModalStore>((set, get) => ({
  ...initialState,

  open: (initialModeOrOptions?: RemixUpgradeModalMode | RemixUpgradeModalOpenOptions) => {
    const openOptions = normalizeOpenOptions(initialModeOrOptions);
    const defaultTab = openOptions.visibleTabs.includes(openOptions.defaultTab)
      ? openOptions.defaultTab
      : openOptions.visibleTabs[0];

    set({ visible: true, mode: defaultTab, openOptions });
    void get().refresh();
  },

  close: () =>
    set({
      visible: false,
      selectedCredits: CREDITS_PER_UNIT,
    }),

  setMode: mode => set({ mode }),

  setSelectedCredits: selectedCredits => set({ selectedCredits }),

  resetCreditPurchaseQuota: () => {
    quotaRequestId += 1;
    set({
      creditPurchaseQuota: null,
      quota: null,
    });
  },

  refresh: async () => {
    const requestId = ++quotaRequestId;
    set({ loading: true, error: null });

    const { productInfo } = get();
    if (!productInfo && !productRequest) {
      productRequest = fetchProduct().finally(() => {
        productRequest = null;
      });
    }

    const [productResult, quotaRes] = await Promise.all([
      productInfo
        ? Promise.resolve({ ok: true, productInfo, product: get().product })
        : productRequest!,
      getCreditPurchaseQuotaRequest(),
    ]);

    if (requestId !== quotaRequestId) {
      return false;
    }

    if (productResult.ok && productResult.productInfo) {
      set({
        product: productResult.product,
        productInfo: productResult.productInfo,
      });
    }

    if (quotaRes.code !== 0 || !quotaRes.data) {
      set({ loading: false, error: 'Failed to fetch data' });
      return false;
    }

    const quota = buildQuota(quotaRes.data.purchased);

    if (!productResult.ok || !productResult.productInfo) {
      set({ loading: false, error: 'Failed to fetch data' });
      return false;
    }

    set({
      creditPurchaseQuota: quotaRes.data,
      product: productResult.product,
      productInfo: productResult.productInfo,
      quota,
      loading: false,
      error: null,
    });

    const { selectedCredits } = get();
    if (selectedCredits > quota.remaining && quota.remaining > 0) {
      set({ selectedCredits: quota.remaining });
    }
    return true;
  },
}));
